/**
 * Putting an app on the internet.
 *
 * One route: POST /apps/{appId}/web, which takes the document itself. There is no S3
 * presign and no direct-to-bucket PUT any more — one path, so there is no second way for a
 * document to reach the internet and no direct-bucket link to leak.
 *
 * The 300KB cap is checked HERE before the round trip, so someone is told their app is too
 * large by the thing they clicked rather than by a failure after a long upload. It counts
 * BYTES, matching the server's own Buffer.byteLength check exactly — approximating it with
 * string length would disagree with the server on every app containing an em dash.
 */
import type { AppDefinition } from '@/types/app';
import { createApp } from './apps';
import { ApiError, request } from './client';

/** What `fetch` actually puts on the wire, and what the server measures. */
export function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** The server's limit, mirrored so the refusal can happen before the upload. */
export const MAX_DOCUMENT_BYTES = 300_000;

export interface HostedApp {
  webPublished: boolean;
  webVisibility: 'public' | 'password';
  slug: string;
  /** The server confirming it actually STORED the document. */
  htmlHosted?: boolean;
  siteUrl?: string;
}

export interface HostInput {
  appId: string;
  html: string;
  visibility?: 'public' | 'password';
  password?: string;
  schemas?: unknown;
  aiAccess?: string;
}

export async function hostApp(input: HostInput): Promise<HostedApp> {
  if (!input.appId) {
    throw new ApiError('misconfigured', 'This app has no id to publish under.');
  }

  const bytes = byteLength(input.html);
  if (bytes > MAX_DOCUMENT_BYTES) {
    const kb = Math.ceil(bytes / 1000);
    throw new ApiError(
      'refused',
      `This app is too large to publish — it is ${kb}KB and the limit is 300KB. Removing some seeded content usually brings it under.`,
    );
  }

  const hosted = await request<HostedApp>(`/apps/${encodeURIComponent(input.appId)}/web`, {
    method: 'POST',
    body: {
      visibility: input.visibility ?? 'public',
      ...(input.password ? { password: input.password } : {}),
      html: input.html,
      ...(input.schemas ? { schemas: input.schemas } : {}),
      ...(input.aiAccess ? { aiAccess: input.aiAccess } : {}),
    },
  });

  /*
   * Do not trust siteUrl without htmlHosted. The server sends the URL either way, and a URL
   * for a document that was not stored is a link to an empty app — worse than no link,
   * because the owner shares it.
   */
  if (!hosted.htmlHosted) {
    throw new ApiError('server', 'The app was registered but its page was not stored. Try publishing again.');
  }

  return hosted;
}

export interface PublishedApp {
  /** The catalogue id this app is published under. Store it on the project. */
  appId: string;
  /** The address to bookmark. */
  siteUrl: string;
}

/**
 * The whole publish: register the app if it is not registered yet, then host the document.
 *
 * Both halves in one place because doing only the second is a 404, and that is exactly
 * what shipped — every publish from this client posted to `/apps/{document id}/web`, an id
 * the catalogue has never heard of, and the finished app never reached the internet.
 *
 * `hostedAppId` is the catalogue id once an app has one, and it is reused for the life of
 * the app: a second `POST /apps` would mint a second listing, which collides on the slug
 * and gets suffixed, so the published address would move on every re-publish.
 */
export async function publishToSaddle(input: {
  definition: AppDefinition;
  name: string;
  description?: string;
  html: string;
}): Promise<PublishedApp> {
  const existing = input.definition.hostedAppId;
  const register = (): Promise<string> =>
    createApp({ name: input.name, description: input.description, definition: input.definition });

  let appId = existing ?? (await register());
  let hosted: HostedApp;
  try {
    hosted = await hostApp({ appId, html: input.html });
  } catch (err) {
    /*
     * The app this project points at can be gone — deleted from the catalogue, or wiped
     * with an account. Reusing a dead id would fail the publish and leave the owner with
     * a link that 404s, so register a fresh one; the slug is reclaimed if it was freed.
     * Only for a MISSING app: a refusal, a 403 or a network failure must surface as
     * itself rather than quietly minting a second listing.
     */
    if (!existing || !(err instanceof ApiError && err.kind === 'not_found')) throw err;
    appId = await register();
    hosted = await hostApp({ appId, html: input.html });
  }

  if (!hosted.siteUrl) {
    throw new ApiError('server', 'The app was published but came back without an address. Try again.');
  }
  return { appId, siteUrl: hosted.siteUrl };
}
